import type { FC, ReactNode } from 'react'

import { CircleCheck, User } from 'lucide-react'
import { COORDINATOR_COLOR, coordinator, teams } from '@/constants/agents'

const tasks: Record<string, string[]> = {
    marketing: [
        "Launch blog post and landing page copy",
        "6 social posts scheduled for launch day"
    ],
    leads: [
        "200 prospects that match your best customers",
        "Every lead scored and added to your CRM"
    ],
    email: [
        "Personal launch email for every trial user",
        "3 step follow-up for the new prospects"
    ],
    bugs: [
        "Checkout bug reproduced, fixed and reviewed",
        "Full test run passed before release"
    ]
}

const points = [
    "You write one message, not twenty prompts.",
    "The Coordinator decides who does what and in what order.",
    "Teams share context, so emails match the launch post and leads match the campaign.",
    "You get one summary back with everything that got done."
]

const ExampleSection: FC = (): ReactNode => {
    const CoordinatorIcon = coordinator.icon

    return (
        <section id='example' className='relative isolate overflow-hidden py-24 sm:py-32'>
            <div
                aria-hidden='true'
                className='absolute left-1/2 top-1/2 -z-10 size-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,oklch(0.74_0.16_292/0.12),transparent_70%)]'
            />

            <div className='mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20'>
                <div>
                    <p className='font-mono text-xs uppercase tracking-[0.2em] text-primary'>
                        Example
                    </p>
                    <h2 className='mt-4 font-heading text-3xl font-semibold tracking-tight text-balance sm:text-5xl'>
                        One message. A whole launch, handled.
                    </h2>
                    <p className='mt-5 text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg'>
                        Here is what happens when you ask the Coordinator to get a new release out the door.
                    </p>

                    <ul className='mt-8 space-y-4'>
                        {points.map((point) => (
                            <li key={point} className='flex gap-3'>
                                <CircleCheck className='mt-0.5 size-5 shrink-0 text-primary' aria-hidden='true' />
                                <span className='leading-relaxed text-foreground/90'>
                                    {point}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='rounded-3xl border border-white/10 bg-card/80 p-4 shadow-2xl shadow-black/30 backdrop-blur sm:p-6'>
                    <div className='flex items-center justify-between border-b border-white/10 pb-4'>
                        <p className='text-sm font-medium'>
                            Chat with the Coordinator
                        </p>
                        <span className='inline-flex items-center gap-1.5 text-xs text-muted-foreground'>
                            <span className='size-2 rounded-full bg-[#34d399] shadow-[0_0_8px_#34d399]' aria-hidden='true' />
                            20 agents available
                        </span>
                    </div>

                    <div className='mt-5 flex justify-end gap-3'>
                        <p className='max-w-[85%] rounded-2xl rounded-tr-sm bg-primary px-4 py-3 text-sm leading-relaxed text-primary-foreground'>
                            We ship v2 next Tuesday. Announce it, find 200 new prospects, email our trial users and make sure the checkout bug is gone before launch.
                        </p>
                        <span className='flex size-8 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/5'>
                            <User className='size-4' aria-hidden='true' />
                        </span>
                    </div>

                    <div className='mt-5 flex gap-3'>
                        <span
                            className='flex size-8 shrink-0 items-center justify-center rounded-full text-white'
                            style={{ backgroundImage: 'radial-gradient(circle at 35% 30%, #ddd0ff, #8b5cf6 55%, #4c1d95)', boxShadow: `0 0 18px ${COORDINATOR_COLOR}80` }}
                        >
                            <CoordinatorIcon className='size-4' aria-hidden='true' />
                        </span>

                        <div className='min-w-0 flex-1 rounded-2xl rounded-tl-sm border border-white/10 bg-white/[0.03] p-4'>
                            <p className='text-sm leading-relaxed'>
                                On it. I split this into 8 tasks across all four teams. Here is where everything landed:
                            </p>

                            <ul className='mt-4 space-y-3'>
                                {teams.map((team) => (
                                    <li key={team.id} className='rounded-xl border border-white/[0.06] bg-background/60 p-3'>
                                        <p className='flex items-center gap-2 text-xs font-semibold uppercase tracking-wider' style={{ color: team.color }}>
                                            <span className='size-1.5 rounded-full' style={{ backgroundColor: team.color }} aria-hidden='true' />
                                            {team.name}
                                        </p>

                                        <ul className='mt-2 space-y-1.5'>
                                            {tasks[team.id].map((task) => (
                                                <li key={task} className='flex items-start gap-2 text-sm text-foreground/90'>
                                                    <CircleCheck className='mt-0.5 size-4 shrink-0 text-[#34d399]' aria-hidden='true' />
                                                    {task}
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default ExampleSection
